import React, { useState } from 'react';
import {
  Container,
  CssBaseline,
  ThemeProvider,
  createTheme,
  AppBar,
  Toolbar,
  Typography,
  Box,
  Alert,
  Snackbar,
  CircularProgress,
  Backdrop,
  Tabs,
  Tab,
  Dialog,
  IconButton,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import GroupsIcon from '@mui/icons-material/Groups';
import LogoutIcon from '@mui/icons-material/Logout';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import { Button } from '@mui/material';
import SearchForm from './components/SearchForm';
import ResultsTable from './components/ResultsTable';
import StatsPanel from './components/StatsPanel';
import CoachPanel from './components/CoachPanel';
import LoginPage from './components/LoginPage';
import athleteService from './services/athleteService';
import { auth } from './services/api';

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#1d63b8',
      dark: '#164a89',
      light: '#4a86c8',
    },
    background: {
      default: '#f4f6f8',
      paper: '#ffffff',
    },
    text: {
      primary: '#1f2937',
      secondary: '#5b6472',
    },
    divider: '#e3e7ec',
  },
  shape: {
    borderRadius: 10,
  },
  typography: {
    fontFamily: [
      '"Noto Sans TC"',
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      '"Microsoft JhengHei"',
      '"微軟正黑體"',
      'sans-serif',
    ].join(','),
    h5: { fontWeight: 700 },
    h6: { fontWeight: 600 },
    subtitle1: { fontWeight: 600 },
    button: { fontWeight: 600, textTransform: 'none' },
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          border: '1px solid #e3e7ec',
          boxShadow: 'none',
        },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          fontWeight: 600,
          backgroundColor: '#f8fafc',
          color: '#5b6472',
          whiteSpace: 'nowrap',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: { fontWeight: 600 },
      },
    },
  },
});

function App() {
  const [user, setUser] = useState(() => (auth.getToken() ? auth.getUser() : null));
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchKey, setSearchKey] = useState(0); // 用於觸發 ResultsTable 重置
  const [tab, setTab] = useState(0);
  const [prefillName, setPrefillName] = useState('');
  const [collapseOnSearch, setCollapseOnSearch] = useState(false);
  const [introOpen, setIntroOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info',
  });

  const handleSearch = async (filters) => {
    // Check if at least one filter is provided
    if (Object.keys(filters).length === 0) {
      setSnackbar({
        open: true,
        message: '請至少輸入一個查詢條件',
        severity: 'warning',
      });
      return;
    }

    setLoading(true);
    setError(null);
    setResults([]); // 先清空舊結果，確保 ResultsTable 的 useEffect 會觸發
    setSearchKey(prev => prev + 1); // 更新搜尋鍵，觸發 ResultsTable 重置

    try {
      const response = await athleteService.searchGameResults(filters);

      if (response.success) {
        setResults(response.data);
        setSnackbar({
          open: true,
          message: `查詢成功！找到 ${response.count} 筆資料`,
          severity: 'success',
        });
      } else {
        setError(response.message || '查詢失敗');
        setSnackbar({
          open: true,
          message: response.message || '查詢失敗',
          severity: 'error',
        });
      }
    } catch (err) {
      const errorMessage =
        err.response?.data?.message || err.message || '查詢時發生錯誤';
      setError(errorMessage);
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error',
      });
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  // 從統計分析點選手姓名時，切回查詢分頁並自動以姓名查詢，結果預設團體收合
  const handleSearchAthlete = (name) => {
    if (!name) return;
    setTab(0);
    setPrefillName(name);
    setCollapseOnSearch(true);
    handleSearch({ athlete_name: name });
  };

  // 手動查詢時恢復個人檢視預設
  const handleManualSearch = (filters) => {
    setCollapseOnSearch(false);
    handleSearch(filters);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleLogin = (token, userInfo) => {
    auth.save(token, userInfo);
    setUser(userInfo);
  };

  const handleLogout = () => {
    auth.clear();
    setUser(null);
  };

  if (!user) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <LoginPage onLogin={handleLogin} />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />

      {/* App Bar */}
      <AppBar
        position="static"
        elevation={0}
        sx={{
          bgcolor: 'background.paper',
          color: 'text.primary',
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Toolbar>
          <Box
            component="img"
            src="/favicon.svg"
            alt=""
            sx={{ width: 36, height: 36, mr: 1.5 }}
          />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            運動員查詢系統
          </Typography>
          <Button
            size="small"
            color="primary"
            startIcon={<PlayCircleOutlineIcon />}
            onClick={() => setIntroOpen(true)}
            sx={{ mr: 2 }}
          >
            介紹影片
          </Button>
          <Typography variant="body2" color="text.secondary" sx={{ mr: 2 }}>
            {user.name || user.email}
          </Typography>
          <Button
            size="small"
            color="inherit"
            startIcon={<LogoutIcon />}
            onClick={handleLogout}
          >
            登出
          </Button>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 3 }}>
          <Tab icon={<SearchIcon />} iconPosition="start" label="運動員查詢" />
          <Tab icon={<QueryStatsIcon />} iconPosition="start" label="統計分析" />
          <Tab icon={<GroupsIcon />} iconPosition="start" label="教練成效" />
        </Tabs>

        {tab === 0 && (
          <>
            {/* Search Form */}
            <SearchForm onSearch={handleManualSearch} loading={loading} prefillName={prefillName} />

            {/* Error Alert */}
            {error && (
              <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            {/* Results Table */}
            <ResultsTable
              results={results}
              searchKey={searchKey}
              collapseOnSearch={collapseOnSearch}
            />
          </>
        )}

        {tab === 1 && <StatsPanel onSearchAthlete={handleSearchAthlete} />}

        {tab === 2 && <CoachPanel onSearchAthlete={handleSearchAthlete} />}
      </Container>

      {/* 系統介紹影片（video-studio 的 athlete-demo 輸出） */}
      <Dialog
        open={introOpen}
        onClose={() => setIntroOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { bgcolor: '#070d1a', border: 'none', position: 'relative' } }}
      >
        <IconButton
          onClick={() => setIntroOpen(false)}
          sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1, color: 'rgba(255,255,255,0.8)' }}
        >
          <CloseIcon />
        </IconButton>
        <Box
          component="video"
          src="/intro.mp4?v=8" // 換影片時遞增版本號，避免瀏覽器播快取裡的舊檔
          controls
          autoPlay
          playsInline
          sx={{ width: '100%', display: 'block', aspectRatio: '16 / 9' }}
        />
      </Dialog>

      {/* Loading Backdrop */}
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress color="inherit" />
          <Typography variant="h6" sx={{ mt: 2 }}>
            查詢中，請稍候...
          </Typography>
        </Box>
      </Backdrop>

      {/* Snackbar Notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </ThemeProvider>
  );
}

export default App;
